import { formatDateToUtcIsoString, formatPercentage } from '@toggle/helpers';
import { Entity, mapEntity } from '@toggle/toggle';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useOneDayReturnLivePrice } from '~/hooks/use-one-day-return-live-price/useOneDayReturnLivePrice';
import { useFetchSnakeByName } from '~/react-query-hooks/snakes/useFetchSnakeByName';
import {
  getOneDayReactionDate,
  getOneDayReactionValue,
} from '~/widgets/earnings/utils/utils';

import * as S from './company-card/CompanyCard.styles';
import { formatDateToET } from './utils';

const ONE_DAY_RETURN_SNAKE = 'mom(horiz=1d)';

interface EarningsPillProps {
  isReported: boolean;
  entity: Entity;
  reportingDate: string;
}

export const EarningsPill = ({
  isReported,
  entity,
  reportingDate,
}: EarningsPillProps) => {
  const { t } = useTranslation(['earnings']);
  const mappedEntity = useMemo(() => mapEntity(entity), [entity]);
  const { oneDayLiveReturn } = useOneDayReturnLivePrice(mappedEntity);

  const dateToUse = reportingDate
    ? getOneDayReactionDate(new Date(reportingDate))
    : undefined;

  const earningsDateFormatted = reportingDate
    ? formatDateToET(new Date(reportingDate))
    : undefined;

  const formattedFromDate = dateToUse
    ? formatDateToUtcIsoString(dateToUse)
    : undefined;

  const { data: snakeData } = useFetchSnakeByName(
    `${entity.tag}.${ONE_DAY_RETURN_SNAKE}`,
    undefined,
    formattedFromDate,
    !formattedFromDate
  );

  const oneDayReaction = getOneDayReactionValue(
    dateToUse,
    oneDayLiveReturn,
    snakeData,
    formattedFromDate
  );

  const dateTitleLabel = isReported
    ? t('earnings:earningsInsight.drawer.earningsPreview.reported')
    : t('earnings:earningsInsight.drawer.earningsPreview.scheduled');

  return (
    <>
      {earningsDateFormatted && (
        <S.EarningsSection $isReported={isReported}>
          <S.EarningsDateWrapper>
            <S.EarningsDateTitleLabel>
              {dateTitleLabel}
            </S.EarningsDateTitleLabel>
            <S.EarningsDateValueLabel>
              {earningsDateFormatted}
            </S.EarningsDateValueLabel>
          </S.EarningsDateWrapper>
          {isReported && oneDayReaction !== undefined && (
            <S.EarningsReactionWrapper>
              <S.EarningsReactionTitleLabel>
                {t('earnings:earningsInsight.drawer.earningsPreview.reaction')}
              </S.EarningsReactionTitleLabel>
              <S.EarningsReactionValueLabel $isPositive={oneDayReaction > 0}>
                {formatPercentage(oneDayReaction, {
                  suffix: '%',
                  addPlus: true,
                  useOriginalValue: true,
                })}
              </S.EarningsReactionValueLabel>
            </S.EarningsReactionWrapper>
          )}
        </S.EarningsSection>
      )}
    </>
  );
};
